import {
  withFieldOrigin,
  wrapFieldValue,
} from 'features/libra/ontology/ontologyFieldDefaults'
import type {
  FieldOrigin,
  FieldValue,
} from 'features/libra/ontology/ontologyFieldTypes'
import { toPersistedJsonShape } from 'features/libra/ontology/persistedJson'
import isEqual from 'lodash/isEqual'
import omit from 'lodash/omit'

import {
  damageMechanismSchema,
  reconcileZoneSnapshot,
  type ZoneParams,
  zoneParamsSchema,
} from './zoneAnalysisApi'
import { normalizeZoneBoundsStorage } from './zoneBoundsStorage'
import { validateZoneGeometryChanges } from './zoneGeometryValidation'
import {
  acknowledgePageZoneOrigins,
  acknowledgeScopedZoneOrigins,
  reconcileZoneOriginsAfterRefresh,
  trackZoneMutation,
  type ZoneOriginTracking,
} from './zoneOriginTracking'

export interface ZoneSnapshot {
  readonly rawZones: unknown
  readonly zones: readonly ZoneParams[]
}

export interface ZoneSaveCapture {
  currentSnapshot: ZoneSnapshot
  savedSnapshot: ZoneSnapshot
  selectedZone: ZoneParams
  selectedBaseline: ZoneParams | undefined
  selectedOrigin: FieldOrigin
  wasPreviouslySaved: boolean
  comparisonBaseline: readonly ZoneParams[]
}

export function isZoneDirty(
  currentZones: readonly ZoneParams[],
  savedZones: readonly ZoneParams[],
  zoneId: string
): boolean {
  const current = currentZones.find(
    (zone) => zone.id === zoneId && !zone.ai_suggested
  )
  return Boolean(
    current &&
    !isEqual(
      current,
      savedZones.find((zone) => zone.id === zoneId)
    )
  )
}

export function captureZoneSave(
  currentSnapshot: ZoneSnapshot,
  savedSnapshot: ZoneSnapshot,
  originTracking: ZoneOriginTracking,
  zoneId: string
): ZoneSaveCapture {
  const selectedZone = currentSnapshot.zones.find((zone) => zone.id === zoneId)
  if (!selectedZone) {
    throw new Error('This zone is no longer available. Refresh and retry.')
  }
  if (selectedZone.ai_suggested) {
    throw new Error('Confirm this AI-suggested zone before saving it.')
  }

  const selectedBaseline = savedSnapshot.zones.find(
    (zone) => zone.id === zoneId
  )
  if (!isZoneDirty(currentSnapshot.zones, savedSnapshot.zones, zoneId)) {
    throw new Error('This zone has no unsaved changes.')
  }

  const wasPreviouslySaved = selectedBaseline !== undefined
  return {
    currentSnapshot,
    savedSnapshot,
    selectedZone,
    selectedBaseline,
    selectedOrigin:
      originTracking.zoneOrigins.get(zoneId)?.scopedFieldOrigin ?? 'human',
    wasPreviouslySaved,
    comparisonBaseline: wasPreviouslySaved
      ? savedSnapshot.zones.map((zone) =>
          zone.id === zoneId ? selectedZone : zone
        )
      : [...savedSnapshot.zones, selectedZone],
  }
}

export function readZoneFieldSnapshot(
  field: FieldValue | undefined,
  identityBaseline: ZoneSnapshot
): {
  snapshot: ZoneSnapshot
  fieldExists: boolean
  hasAmbiguousLegacyChange: boolean
  isValid: boolean
} {
  const fieldExists = field !== undefined
  const rawZones = field?.value
  const parsed = fieldExists
    ? zoneParamsSchema.array().safeParse(rawZones)
    : undefined

  const zones = parsed?.success ? parsed.data : []
  const reconciled = reconcileZoneSnapshot(
    identityBaseline.rawZones,
    identityBaseline.zones,
    rawZones,
    zones
  )
  return {
    snapshot: { rawZones, zones: reconciled.zones },
    fieldExists,
    hasAmbiguousLegacyChange: reconciled.hasAmbiguousLegacyChange,
    isValid: !parsed || parsed.success,
  }
}

export function prepareZoneFieldSave(
  latestField: FieldValue | undefined,
  capture: ZoneSaveCapture,
  latest: ReturnType<typeof readZoneFieldSnapshot> = readZoneFieldSnapshot(
    latestField,
    capture.savedSnapshot
  )
): FieldValue {
  const { snapshot, fieldExists, hasAmbiguousLegacyChange, isValid } = latest
  if (hasAmbiguousLegacyChange) {
    throw new Error(
      'Zone identities changed while saving. Refresh and review the zone list before retrying.'
    )
  }
  if (!isValid) {
    throw new Error(
      'Saved zone data is invalid. Reload the ticket before saving.'
    )
  }
  const rawZones = replaceSavedZone(
    fieldExists,
    snapshot.rawZones,
    capture.selectedZone,
    capture.wasPreviouslySaved,
    capture.savedSnapshot.zones
  )
  const geometryError = validateZoneGeometryChanges({
    baselineZones: snapshot.zones,
    proposedZones: zoneParamsSchema.array().parse(rawZones),
  })
  if (geometryError) throw new Error(geometryError)

  const wrapper = latestField
    ? { ...latestField, value: rawZones }
    : wrapFieldValue(rawZones)
  return withFieldOrigin(wrapper, capture.selectedOrigin)
}

export interface ZoneSaveSnapshotReconciliation {
  readonly currentSnapshot: {
    readonly rawZones: unknown
    readonly zones: ZoneParams[]
  }
  readonly savedSnapshot: ZoneSnapshot
  readonly originTracking: ZoneOriginTracking
}

export function reconcileZoneSnapshotOrigins(
  originTracking: ZoneOriginTracking,
  previousCurrentSnapshot: ZoneSnapshot,
  currentSnapshot: ZoneSnapshot,
  previousSavedSnapshot: ZoneSnapshot,
  savedSnapshot: ZoneSnapshot
): ZoneOriginTracking {
  if (!isEqual(previousSavedSnapshot.zones, savedSnapshot.zones)) {
    return {
      ...originTracking,
      zoneOrigins: reconcileZoneOriginsAfterRefresh(
        originTracking.zoneOrigins,
        currentSnapshot.zones,
        previousSavedSnapshot.zones,
        savedSnapshot.zones
      ),
    }
  }

  const tracked = trackZoneMutation(
    originTracking,
    previousCurrentSnapshot.zones,
    currentSnapshot.zones
  )
  let zoneOrigins = tracked.zoneOrigins
  for (const zoneId of zoneOrigins.keys()) {
    zoneOrigins = acknowledgeScopedZoneOrigins(
      zoneOrigins,
      currentSnapshot.zones,
      savedSnapshot.zones,
      savedSnapshot.zones,
      zoneId
    )
  }
  return { ...tracked, zoneOrigins }
}

export interface PersistedZoneSnapshot extends ZoneSnapshot {
  readonly justification: string | undefined
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function matchesPersistedZoneSnapshot(
  snapshot: PersistedZoneSnapshot,
  candidate: PersistedZoneSnapshot
): boolean {
  return (
    snapshot.justification === candidate.justification &&
    isEqual(snapshot.rawZones, candidate.rawZones) &&
    isEqual(
      snapshot.zones,
      reconcileZoneSnapshot(
        snapshot.rawZones,
        snapshot.zones,
        candidate.rawZones,
        candidate.zones
      ).zones
    )
  )
}

function mergeEditedValue(
  rawValue: unknown,
  baseline: object,
  edited: object
): Record<string, unknown> {
  const next = isRecord(rawValue) ? { ...rawValue } : {}
  const keys = new Set([...Object.keys(baseline), ...Object.keys(edited)])

  for (const key of keys) {
    const baselineValue = Object.hasOwn(baseline, key)
      ? Reflect.get(baseline, key)
      : undefined
    const editedValue = Object.hasOwn(edited, key)
      ? Reflect.get(edited, key)
      : undefined
    if (isEqual(baselineValue, editedValue)) continue

    if (editedValue === undefined) {
      delete next[key]
    } else {
      const rawChild = Object.hasOwn(next, key)
        ? Reflect.get(next, key)
        : undefined
      const value = isRecord(editedValue)
        ? mergeEditedValue(
            rawChild,
            isRecord(baselineValue) ? baselineValue : {},
            editedValue
          )
        : toPersistedJsonShape(editedValue)
      Object.defineProperty(next, key, {
        value,
        enumerable: true,
        configurable: true,
        writable: true,
      })
    }
  }

  return next
}

function prepareZoneForSave(
  rawZone: unknown,
  previousZone: ZoneParams | undefined,
  nextZone: ZoneParams
): unknown {
  const prepared =
    rawZone !== undefined && previousZone
      ? mergeEditedValue(rawZone, previousZone, nextZone)
      : nextZone
  const result = isRecord(prepared) ? { ...prepared } : {}
  result.id = nextZone.id

  const baselineMechanisms = previousZone?.damage_mechanisms ?? []
  const rawMechanisms = result.damage_mechanisms
  if (
    Array.isArray(rawMechanisms) &&
    isEqual(baselineMechanisms, nextZone.damage_mechanisms)
  ) {
    const normalizedRawMechanisms = rawMechanisms.map((mechanism) => {
      const parsed = damageMechanismSchema.safeParse(mechanism)
      return parsed.success ? omit(parsed.data, 'id') : undefined
    })
    const normalizedBaselineMechanisms = baselineMechanisms.map((mechanism) =>
      omit(mechanism, 'id')
    )

    if (isEqual(normalizedRawMechanisms, normalizedBaselineMechanisms)) {
      result.damage_mechanisms = rawMechanisms.map((mechanism, index) => {
        const baseline = baselineMechanisms[index]
        return isRecord(mechanism) && mechanism.id === undefined && baseline
          ? { ...mechanism, id: baseline.id }
          : mechanism
      })
    }
  }

  return normalizeZoneBoundsStorage([result])[0]
}

export function prepareZonesForSave({
  rawZones,
  previousZones,
  latestZones,
  proposedZones,
}: {
  rawZones: unknown
  previousZones: readonly ZoneParams[]
  latestZones: readonly ZoneParams[]
  proposedZones: readonly ZoneParams[]
}): unknown[] {
  const raw = Array.isArray(rawZones) ? rawZones : []
  const latestById = new Map(latestZones.map((zone, index) => [zone.id, index]))
  const previousById = new Map(previousZones.map((zone) => [zone.id, zone]))

  return proposedZones.map((zone) => {
    const latestIndex = latestById.get(zone.id)
    return prepareZoneForSave(
      latestIndex === undefined ? undefined : raw[latestIndex],
      previousById.get(zone.id),
      zone
    )
  })
}

export function hasDuplicateStoredZoneIds(rawZones: unknown): boolean {
  if (!Array.isArray(rawZones)) return false
  const ids = rawZones
    .filter(isRecord)
    .map((zone) => zone.id)
    .filter((id): id is string => typeof id === 'string' && id.length > 0)
  return new Set(ids).size !== ids.length
}

export function hasEmptyStoredZoneId(rawZones: unknown): boolean {
  return (
    Array.isArray(rawZones) &&
    rawZones.some((zone) => isRecord(zone) && zone.id === '')
  )
}

export function stageZoneSnapshot(
  rawZones: unknown,
  previousZones: readonly ZoneParams[],
  nextZones: readonly ZoneParams[],
  originTracking: ZoneOriginTracking,
  materializeIds = false
): { snapshot: ZoneSnapshot; originTracking: ZoneOriginTracking } | undefined {
  const raw = Array.isArray(rawZones) ? rawZones : []
  const previousById = new Map(previousZones.map((zone) => [zone.id, zone]))
  const previousIndexById = new Map(
    previousZones.map((zone, index) => [zone.id, index])
  )
  const rawById = new Map<string, Record<string, unknown>>()
  for (const record of raw) {
    if (!isRecord(record)) continue
    const id = Object.hasOwn(record, 'id') ? record.id : undefined
    if (typeof id !== 'string') continue
    if (rawById.has(id)) return undefined
    rawById.set(id, record)
  }

  const nextRawZones = nextZones.map((zone) => {
    const existing =
      rawById.get(zone.id) ?? raw[previousIndexById.get(zone.id) ?? -1]
    const previous = previousById.get(zone.id)
    if (!isRecord(existing) || !previous) return zone
    return isEqual(zone, previous) && !materializeIds
      ? existing
      : prepareZoneForSave(existing, previous, zone)
  })

  return {
    snapshot: { rawZones: nextRawZones, zones: nextZones },
    originTracking: trackZoneMutation(originTracking, previousZones, nextZones),
  }
}

export function reconcileZoneSaveSnapshot({
  currentFieldExists,
  currentRawZones,
  currentDraftZones,
  appliedFieldExists,
  appliedRawZones,
  originTracking,
  scope,
}: {
  currentFieldExists: boolean
  currentRawZones: unknown
  currentDraftZones?: readonly ZoneParams[]
  appliedFieldExists?: boolean
  appliedRawZones: unknown
  originTracking: ZoneOriginTracking
  scope:
    | { kind: 'zone'; capture: ZoneSaveCapture }
    | {
        kind: 'page'
        submittedSnapshot: ZoneSnapshot
        comparisonBaseline: readonly ZoneParams[]
        submittedMutationOrder: number
      }
}): ZoneSaveSnapshotReconciliation {
  const submittedSnapshot =
    scope.kind === 'zone'
      ? scope.capture.currentSnapshot
      : scope.submittedSnapshot
  const comparisonBaseline =
    scope.kind === 'zone'
      ? scope.capture.comparisonBaseline
      : scope.comparisonBaseline
  const { currentSnapshot, savedSnapshot } = readZoneSaveSnapshots(
    submittedSnapshot,
    currentFieldExists,
    currentRawZones,
    appliedFieldExists,
    appliedRawZones
  )
  const zones = reconcileZoneDrafts(
    comparisonBaseline,
    currentDraftZones ?? currentSnapshot.zones,
    savedSnapshot.zones
  )
  const zoneOrigins =
    scope.kind === 'zone'
      ? acknowledgeScopedZoneOrigins(
          originTracking.zoneOrigins,
          zones,
          savedSnapshot.zones,
          scope.capture.savedSnapshot.zones,
          scope.capture.selectedZone.id
        )
      : acknowledgePageZoneOrigins(
          originTracking.zoneOrigins,
          zones,
          savedSnapshot.zones,
          scope.submittedMutationOrder
        )

  return {
    currentSnapshot: {
      rawZones: reconcileZoneRawRecords(currentSnapshot, savedSnapshot, zones),
      zones,
    },
    savedSnapshot,
    originTracking: { ...originTracking, zoneOrigins },
  }
}

function readZoneSaveSnapshots(
  submittedSnapshot: ZoneSnapshot,
  currentFieldExists: boolean,
  currentRawZones: unknown,
  appliedFieldExists: boolean | undefined,
  appliedRawZones: unknown
): { currentSnapshot: ZoneSnapshot; savedSnapshot: ZoneSnapshot } {
  const current = readZoneFieldSnapshot(
    currentFieldExists ? wrapFieldValue(currentRawZones) : undefined,
    submittedSnapshot
  )
  const applied = readZoneFieldSnapshot(
    appliedFieldExists === false ? undefined : wrapFieldValue(appliedRawZones),
    submittedSnapshot
  )
  if (!current.isValid || !applied.isValid) {
    throw new Error(
      'Saved zone data is invalid. Reload the ticket before saving.'
    )
  }
  if (current.hasAmbiguousLegacyChange || applied.hasAmbiguousLegacyChange) {
    throw new Error(
      'Zone identities changed while saving. Refresh and review the zone list before retrying.'
    )
  }

  return {
    currentSnapshot: current.snapshot,
    savedSnapshot: applied.snapshot,
  }
}

function reconcileZoneRawRecords(
  currentSnapshot: ZoneSnapshot,
  savedSnapshot: ZoneSnapshot,
  zones: readonly ZoneParams[]
): unknown[] {
  const currentRawById = rawZonesById(currentSnapshot.rawZones)
  const appliedRawById = rawZonesById(savedSnapshot.rawZones)
  const currentById = new Map(
    currentSnapshot.zones.map((zone) => [zone.id, zone])
  )
  const appliedById = new Map(
    savedSnapshot.zones.map((zone) => [zone.id, zone])
  )
  return zones.map((zone) => {
    const appliedRaw = appliedRawById.get(zone.id)
    const currentRaw = currentRawById.get(zone.id)
    if (appliedRaw && isEqual(zone, appliedById.get(zone.id))) return appliedRaw
    if (!currentRaw) return zone
    const currentZone = currentById.get(zone.id)
    return currentZone && isEqual(zone, currentZone)
      ? currentRaw
      : currentZone
        ? prepareZoneForSave(currentRaw, currentZone, zone)
        : zone
  })
}

function rawZonesById(rawZones: unknown): Map<string, Record<string, unknown>> {
  const zones = new Map<string, Record<string, unknown>>()
  if (!Array.isArray(rawZones)) return zones
  for (const record of rawZones) {
    if (!isRecord(record)) continue
    const id = Object.hasOwn(record, 'id') ? record.id : undefined
    if (typeof id === 'string') zones.set(id, record)
  }
  return zones
}

export function replaceSavedZone(
  fieldExists: boolean,
  rawZones: unknown,
  selectedZone: ZoneParams,
  wasPreviouslySaved = false,
  baselineZones?: readonly ZoneParams[]
): unknown[] {
  if (selectedZone.ai_suggested) {
    throw new Error('Confirm this AI-suggested zone before saving it.')
  }
  if (!fieldExists) {
    if (wasPreviouslySaved) {
      throw new Error(
        'The selected saved zone is missing from the ticket. Refresh and review the zone list before saving.'
      )
    }
    return [prepareZoneForSave(undefined, undefined, selectedZone)]
  }

  if (!Array.isArray(rawZones)) {
    throw new Error(
      'Saved zone data is invalid. Reload the ticket before saving.'
    )
  }

  const validated = zoneParamsSchema.array().safeParse(rawZones)
  if (!validated.success) {
    throw new Error(
      'Saved zone data is invalid. Reload the ticket and correct the zone data before saving.'
    )
  }

  const ids = rawZones.map((raw) =>
    isRecord(raw) && Object.hasOwn(raw, 'id') ? raw.id : undefined
  )
  if (ids.some((id) => typeof id !== 'string' || id.length === 0)) {
    throw new Error(
      'A saved zone is missing its stored ID. Use page Save to persist all page changes and write stable zone IDs, then retry Save zone.'
    )
  }
  if (new Set(ids).size !== ids.length) {
    throw new Error(
      'Saved zones contain duplicate IDs. Have the stored IDs repaired before using Save zone.'
    )
  }

  const selectedIndex = validated.data.findIndex(
    (zone) => zone.id === selectedZone.id
  )
  if (selectedIndex < 0) {
    if (wasPreviouslySaved) {
      throw new Error(
        'The selected saved zone is no longer present on the ticket. Refresh and review the zone list before saving.'
      )
    }
    return [...rawZones, prepareZoneForSave(undefined, undefined, selectedZone)]
  }

  return rawZones.map((raw, index) =>
    index === selectedIndex
      ? prepareZoneForSave(
          raw,
          baselineZones?.find((zone) => zone.id === selectedZone.id) ??
            validated.data[selectedIndex],
          selectedZone
        )
      : raw
  )
}

export function reconcileZoneDrafts(
  comparisonBaseline: readonly ZoneParams[],
  currentDraft: readonly ZoneParams[],
  nextSaved: readonly ZoneParams[]
): ZoneParams[] {
  const baselineById = new Map(
    comparisonBaseline.map((zone) => [zone.id, zone])
  )
  const draftById = new Map(currentDraft.map((zone) => [zone.id, zone]))
  const nextIds = new Set(nextSaved.map((zone) => zone.id))
  const nextDraft: ZoneParams[] = []

  const keepDraft = (zone: ZoneParams | undefined, id: string) =>
    zone?.ai_suggested && nextIds.has(id)
      ? false
      : !isEqual(zone, baselineById.get(id))

  for (const saved of nextSaved) {
    const draft = draftById.get(saved.id)
    if (keepDraft(draft, saved.id)) {
      if (draft) nextDraft.push(draft)
    } else {
      nextDraft.push(saved)
    }
  }

  for (const draft of currentDraft) {
    if (!nextIds.has(draft.id) && keepDraft(draft, draft.id)) {
      nextDraft.push(draft)
    }
  }

  return nextDraft
}
